import {authApi} from '@/api/auth.api.js'
import {bearerToken, csrfToken, isAuthenticated, reAuthenticationHandler} from '@/services/auth-session.js'
import {useTeams} from '@/composables/use-teams.js'
import {useAuth0} from '@/composables/use-auth0.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useRailsUrls} from '@/composables/use-rails-urls.js'
import {useEmployeePortal} from '@/composables/use-employee-portal.js'
import {useCurrentUser} from '@/composables/use-current-user.js'
import {joinPaths, openUrl} from '@/helpers/url.js'
import {ref, onMounted, onBeforeUnmount} from 'vue'

//Global refs
const authPromise = ref(null)

//Cross-tab logout sync — module-scoped so every composable instance shares a single subscription.
//Null on environments without BroadcastChannel support (older browsers, tests).
const CHANNEL_NAME = 'messenger-auth'
const LOGOUT_MESSAGE_TYPE = 'logout'
const broadcastChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null

/**
 * Authentication composable
 */
export function useAuthentication() {

  //Composables
  const {hasInitializedTeams, isInTeams, getTeamsAuthToken, userEmail} = useTeams()
  const {getAuth0Token, loginWithAuth0} = useAuth0()
  const {usesEmployeePortal} = useEmployeePortal()
  const {goToEmployeeLogin} = useNavigation()
  const {railsAppRootUrl} = useRailsUrls()
  const {clearUser} = useCurrentUser()

  //Process auth data
  const processAuthData = json => {

    //Store CSRF token and mark as authenticated
    bearerToken.value = json.token
    isAuthenticated.value = true
  }

  //Authenticate through Teams
  const authenticateWithTeams = async () => {

    //Must have initialized Teams before calling this
    if (!hasInitializedTeams.value) {
      throw new Error('Please call `initializeTeams()` first before trying to authenticate with Teams')
    }

    //Get token and authenticate
    const token = await getTeamsAuthToken()
    const json = await authApi.authenticateWithTeamsSSOToken(token, {
      userEmail: userEmail.value,
    })

    //Process auth data
    processAuthData(json)
  }

  //Authenticate
  const authenticate = async () => {

    //Already authenticating
    if (authPromise.value) {
      return authPromise.value
    }

    //Auth runner
    const run = async () => {
      try {
        if (isInTeams.value) {
          await authenticateWithTeams()
        }
        else if (usesEmployeePortal.value) {
          await goToEmployeeLogin()
        }
        else {
          await loginWithAuth0()
        }
      }
      finally {
        authPromise.value = null
      }
    }

    //Store promise and return it
    authPromise.value = run()
    return authPromise.value
  }

  //Set authenticated
  const setAuthenticated = value => isAuthenticated.value = value

  //Obtain CSRF token from the dedicated API endpoint (web mount calls this after session is ready)
  const obtainCsrfToken = async () => {
    csrfToken.value = await authApi.getCsrfToken()
  }

  //Obtain Auth0 ID token and persist it to the Rails session cookie
  const syncAuth0Cookie = async () => {
    const token = await getAuth0Token()
    if (token) {
      await authApi.syncAuthCookie(token)
    }
    else {
      throw new Error('Auth0 ID token unavailable')
    }
  }

  //Register re-authentication handler
  const registerReAuthenticationHandler = () => {
    reAuthenticationHandler.value = authenticate
  }

  //Navigate to the messenger logout endpoint (bare navigation, no broadcast).
  //Used both directly and by the cross-tab logout listener so the listener does not re-broadcast.
  const navigateToLogout = () => {
    const logoutUrl = joinPaths(railsAppRootUrl.value, 'messenger/logout')
    openUrl(logoutUrl)
  }

  //Redirect to logout page — broadcasts to other tabs synchronously, then navigates.
  const redirectToLogout = () => {
    broadcastChannel?.postMessage({type: LOGOUT_MESSAGE_TYPE})
    navigateToLogout()
  }

  //Register a listener that ends the local session when another tab logs out.
  //Must be called once from a setup-scoped context (e.g. App.vue) so the
  //onMounted/onBeforeUnmount hooks bind to the host component's lifecycle.
  const registerLogoutListener = () => {
    if (!broadcastChannel) {
      return
    }

    const handleMessage = event => {
      if (event.data?.type === LOGOUT_MESSAGE_TYPE) {
        clearUser()
        navigateToLogout()
      }
    }

    onMounted(() => {
      broadcastChannel.addEventListener('message', handleMessage)
    })
    onBeforeUnmount(() => {
      broadcastChannel.removeEventListener('message', handleMessage)
    })
  }

  //Expose interface
  return {
    authenticate,
    authenticateWithTeams,
    setAuthenticated,
    obtainCsrfToken,
    syncAuth0Cookie,
    registerReAuthenticationHandler,
    redirectToLogout,
    registerLogoutListener,

    //Re-export session refs
    isAuthenticated,
    bearerToken,
    csrfToken,
  }
}
